import { useState } from "react"
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query"
import { Link, createFileRoute } from "@tanstack/react-router"
import { ArrowRight, Clock3 } from "lucide-react"
import { toast } from "sonner"
import type { CampaignDetail } from "@vienna/contracts"
import { CAMPAIGN_STATUS_LABELS } from "@vienna/contracts"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { Header, Hint, Section, SelectField, Stats, Status } from "@/components/audience/primitives"
import { providerLabel } from "@/components/campaigns/helpers"
import { BackLink } from "@/components/campaigns/primitives"
import { api, ApiError } from "@/lib/api"
import { formatDateTime } from "@/lib/format"
import { campaignResultsQuery } from "@/lib/queries"
import { useCan, useWorkspaceContext } from "@/lib/session"
import { wallClock, zoneOptions } from "./w.$workspace.campaigns.$campaignId.schedule"

export const Route = createFileRoute("/w/$workspace/campaigns/$campaignId/results")({
  loader: ({ context, params }) => context.queryClient.ensureQueryData(campaignResultsQuery(params.workspace, params.campaignId)),
  component: CampaignResults,
})

/** Screen 28. Outcomes come from the send ledger through recipient references; nothing here is a copy. */
function CampaignResults() {
  const { workspace } = useWorkspaceContext()
  const can = useCan()
  const { campaignId } = Route.useParams()
  const queryClient = useQueryClient()
  const { campaign, funnel, outcomes, excluded, updatedAt } = useSuspenseQuery(campaignResultsQuery(workspace.slug, campaignId)).data
  const [reschedule, setReschedule] = useState(false)
  const initial = wallClock(campaign.scheduledAt, campaign.timezone)
  const [date, setDate] = useState(initial.date)
  const [time, setTime] = useState(initial.time)
  const [zone, setZone] = useState(campaign.timezone)
  const params = { workspace: workspace.slug, campaignId }
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["workspace", workspace.slug, "campaigns"] })
  const action = useMutation({
    mutationFn: ({ verb, body }: { verb: "cancel" | "resume" | "reschedule"; body?: unknown }) => api<CampaignDetail>(`/w/${workspace.slug}/campaigns/${campaignId}/${verb}`, { method: "POST", ...(body ? { body: JSON.stringify(body) } : {}) }),
    onSuccess: async (detail, { verb }) => {
      await invalidate()
      setReschedule(false)
      toast.success(verb === "cancel" ? "Campaign cancelled" : verb === "resume" ? "Delivery resumed" : `Rescheduled for ${detail.scheduledAt ? formatDateTime(detail.scheduledAt) : "the new time"}`)
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.problem.title : "Could not update the campaign"),
  })
  const stages = [
    ["Selected audience", funnel.selected],
    ["Initially eligible", funnel.eligible],
    ["Provider accepted", funnel.accepted],
    ["Confirmed delivered", funnel.delivered],
    ["Replied", funnel.replied],
  ] as const
  const max = Math.max(1, funnel.selected)
  const attempts = outcomes.delivered + outcomes.awaitingReceipt + outcomes.failed + outcomes.skipped + outcomes.unconfirmed + outcomes.queued + outcomes.cancelled
  const active = ["scheduled", "sending", "attention"].includes(campaign.status)
  return (
    <div className="page-wrap au-root">
      <Header
        title={campaign.name}
        description={`${providerLabel(campaign.provider)} · ${campaign.status === "scheduled" && campaign.scheduledAt ? `Sends ${formatDateTime(campaign.scheduledAt)}` : campaign.startedAt ? `Sent ${formatDateTime(campaign.startedAt)}` : campaign.approvedAt ? `Approved ${formatDateTime(campaign.approvedAt)}` : "Draft"}`}
        back={<BackLink to="/w/$workspace/campaigns" params={{ workspace: workspace.slug }}>Campaigns</BackLink>}
      >
        <Status>{CAMPAIGN_STATUS_LABELS[campaign.status]}</Status>
        {campaign.status === "attention" && can("publish") && (
          <Button variant="outline" onClick={() => action.mutate({ verb: "resume" })} disabled={action.isPending}>
            Resume delivery
          </Button>
        )}
        {campaign.status === "scheduled" && !campaign.startedAt && can("publish") && (
          <Button variant="outline" onClick={() => setReschedule(true)}>
            <Clock3 /> Reschedule
          </Button>
        )}
        {active && can("edit_automations") && (
          <Button variant="outline" onClick={() => action.mutate({ verb: "cancel" })} disabled={action.isPending}>
            Cancel campaign
          </Button>
        )}
        <Button variant="outline" asChild>
          <Link to="/w/$workspace/campaigns/$campaignId/recipients" params={params} search={{}}>
            View recipients <ArrowRight />
          </Link>
        </Button>
      </Header>
      {campaign.status === "attention" && campaign.attentionReason && (
        <div className="mb-6">
          <Hint>{campaign.attentionReason}</Hint>
        </div>
      )}
      {campaign.snapshot?.status === "building" && (
        <div className="mb-6">
          <Hint icon="clock">Preparing the audience against the approval cutoff. Delivery starts when it is ready.</Hint>
        </div>
      )}
      <Stats
        items={[
          { label: "Delivered", value: outcomes.delivered.toLocaleString(), detail: "Confirmed provider receipt" },
          { label: "Awaiting receipt", value: outcomes.awaitingReceipt.toLocaleString(), detail: "Accepted; delivery unconfirmed" },
          { label: "Failed", value: (outcomes.failed + outcomes.unconfirmed).toLocaleString(), detail: outcomes.unconfirmed ? `${outcomes.unconfirmed} unconfirmed need reconciliation` : "Delivery attempt failed" },
          { label: "Skipped at send", value: outcomes.skipped.toLocaleString(), detail: "Eligibility changed" },
        ]}
      />
      <div className="au-two-column mt-7">
        <Section title="From audience to conversation" description="Each stage is a subset of the one above.">
          <div className="space-y-6 pt-3">
            {stages.map(([label, value], index) => (
              <div key={label}>
                <div className="mb-2 flex justify-between text-sm">
                  <span>{label}</span>
                  <span className="font-medium tabular-nums">{value.toLocaleString()}</span>
                </div>
                <div className="h-3 rounded-sm bg-muted">
                  <div className="h-full rounded-sm bg-foreground" style={{ width: `${(value / max) * 100}%`, opacity: 0.2 + index * 0.18 }} />
                </div>
              </div>
            ))}
          </div>
          <p className="mt-6 text-xs text-muted-foreground">
            {excluded.toLocaleString()} {excluded === 1 ? "person was" : "people were"} excluded before delivery and {excluded === 1 ? "is" : "are"} outside the delivery outcomes.
          </p>
        </Section>
        <div className="space-y-5">
          <Section title="Replies">
            <div className="flex items-baseline gap-3">
              <strong className="text-4xl font-semibold tracking-tight">{funnel.replied.toLocaleString()}</strong>
              <span className="text-sm text-muted-foreground">people replied</span>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">{funnel.delivered ? `${((funnel.replied / funnel.delivered) * 100).toFixed(1)}% of delivered recipients` : "Replies are counted among delivered recipients"}</p>
            <Button variant="outline" className="mt-5" asChild>
              <Link to="/w/$workspace/inbox" params={{ workspace: workspace.slug }} search={{}}>
                Open the inbox <ArrowRight />
              </Link>
            </Button>
          </Section>
          <Section title="A clear picture of delivery">
            <p className="text-sm leading-6 text-muted-foreground">
              {funnel.accepted.toLocaleString()} message{funnel.accepted === 1 ? " was" : "s were"} accepted by {providerLabel(campaign.provider)}. {outcomes.delivered.toLocaleString()} {outcomes.delivered === 1 ? "has" : "have"} delivery receipts; {outcomes.awaitingReceipt.toLocaleString()} remain unconfirmed.
            </p>
            <Separator className="my-4" />
            <dl className="au-definition">
              <div>
                <dt>Read data</dt>
                <dd>Unavailable</dd>
              </div>
              <div>
                <dt>Updated</dt>
                <dd>{formatDateTime(updatedAt)}</dd>
              </div>
            </dl>
          </Section>
        </div>
      </div>
      <Hint>
        {attempts.toLocaleString()} attempts = {outcomes.delivered} delivered + {outcomes.awaitingReceipt} awaiting receipt + {outcomes.failed + outcomes.unconfirmed} failed or unconfirmed + {outcomes.skipped} skipped{outcomes.queued ? ` + ${outcomes.queued} queued` : ""}
        {outcomes.cancelled ? ` + ${outcomes.cancelled} cancelled` : ""}. Acceptance and delivery are separate signals.
      </Hint>
      <Dialog open={reschedule} onOpenChange={setReschedule}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reschedule this campaign</DialogTitle>
            <DialogDescription>The frozen audience is kept; every recipient is checked again at the new send time.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="reschedule-date">Date</Label>
              <Input id="reschedule-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reschedule-time">Time</Label>
              <Input id="reschedule-time" type="time" value={time} onChange={(e) => setTime(e.target.value)} />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Time zone</Label>
            <SelectField label="Time zone" value={zone} onChange={setZone} options={zoneOptions(zone)} className="w-full" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => action.mutate({ verb: "reschedule", body: { timing: "now", timezone: zone } })} disabled={action.isPending}>
              Send now
            </Button>
            <Button onClick={() => action.mutate({ verb: "reschedule", body: { timing: "scheduled", date, time, timezone: zone } })} disabled={action.isPending || !date || !time}>
              Confirm new time
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
